import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { Check, Plane } from "lucide-react";
import { useT } from "../lib/i18n";
import { getThinkingMessages } from "../lib/thinkingMessages";

const PROGRESS = {
  ms: ["Menyediakan", "Menyelidik", "Menyusun", "Hampir siap"],
  en: ["Preparing", "Researching", "Building", "Almost ready"],
};

export default function ThinkingAnimation({ persona = null, immersive = false }) {
  const { lang } = useT();
  const reduceMotion = useReducedMotion();
  const states = useMemo(() => getThinkingMessages(persona, lang), [persona, lang]);
  const [index, setIndex] = useState(0);
  const progressIndex = Math.min(3, Math.floor((index / Math.max(1, states.length - 1)) * 4));

  useEffect(() => setIndex(0), [persona, lang]);
  useEffect(() => {
    if (index >= states.length - 1) return undefined;
    const timer = window.setTimeout(
      () => setIndex((current) => Math.min(current + 1, states.length - 1)),
      3200
    );
    return () => window.clearTimeout(timer);
  }, [index, states.length]);

  return (
    <motion.section
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={immersive ? "taip-thinking-stage" : "taip-thinking-bubble"}
      data-testid="thinking"
      data-persona={persona || "default"}
      role="status"
      aria-live="polite"
    >
      <div className="taip-thinking-copy">
        <p className="taip-thinking-kicker">
          {lang === "en" ? "TAi-P is thinking with you" : "TAi-P sedang berfikir bersama anda"}
        </p>
        <AnimatePresence mode="wait">
          <motion.p
            key={`${persona || "default"}-${index}`}
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -5 }}
            transition={{ duration: reduceMotion ? 0 : 0.28 }}
            className="taip-thinking-line"
            data-testid="thinking-line"
          >
            {states[index]}
          </motion.p>
        </AnimatePresence>
      </div>

      <div className="taip-thinking-flight" aria-hidden="true">
        <div className="taip-thinking-route" />
        <motion.div
          className="taip-thinking-plane"
          animate={reduceMotion ? undefined : { x: [0, 72, 146, 220], y: [10, -7, 4, -12], rotate: [-5, 10, -2, 9] }}
          transition={{ duration: 5.2, repeat: Infinity, ease: "easeInOut" }}
        >
          <Plane size={18} strokeWidth={2.2} />
        </motion.div>
      </div>

      <ol className="taip-thinking-progress" aria-label={lang === "en" ? "Planning progress" : "Kemajuan perancangan"}>
        {PROGRESS[lang === "en" ? "en" : "ms"].map((label, step) => (
          <li key={label} className={step <= progressIndex ? "is-active" : ""}>
            <span>{step < progressIndex ? <Check size={11} /> : step + 1}</span>
            <small>{label}</small>
          </li>
        ))}
      </ol>
    </motion.section>
  );
}
